// ProjectSection: agrupa todas las filas (issues, PRs, merges) de un mismo
// proyecto. Header con métricas + identidad coloreada del repo (click = toggle
// pin; en modo carrusel además controles ◂ ▸); debajo N ProjectRow.
// Los tests siguen usando project-header-<slug>.

import { useState } from 'react'
import { Pin, RotateCw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCommits, useIssues, useRecentlyClosedIssues, usePRs, useClosedUnmergedPRs, useRuns, useMergedPRs, getSeverity, type Severity } from '../queries'
import type { Repo } from '../repos'
import { ProjectRow } from './ProjectRow'
import { useAutoLabel } from '../useAutoLabel'
import { togglePin } from '../pinned'
import { rotateSlot } from '../carousel'
import { buildThreads } from '../threads'

// Threads mergeados visibles antes de colapsar bajo "mostrar más". Los issues
// abiertos (accionables) nunca se colapsan; solo el historial de merges.
const MERGED_VISIBLE = 3

type CiStatus = 'success' | 'failure' | 'pending' | 'no-ci'

// Ventana del chip "N·24h" del header (las últimas 24h del dataset de 30d).
const DAY_MS = 86_400_000

interface ProjectSectionProps {
  repo: Repo
  mode?: 'pinned' | 'carousel'
  slotIndex?: number
  carouselTotal?: number
  isFirst?: boolean
}

export function ProjectSection({ repo, mode = 'pinned', slotIndex = 0, carouselTotal, isFirst = false }: ProjectSectionProps) {
  const [expanded, setExpanded] = useState(false)
  const issues = useIssues(repo.slug)
  const closedIssues = useRecentlyClosedIssues(repo.slug)
  const prs = usePRs(repo.slug)
  const closedUnmergedPRs = useClosedUnmergedPRs(repo.slug)
  const runs = useRuns(repo.slug)
  const commits = useCommits(repo.slug)
  const merged = useMergedPRs(repo.slug)

  const issueList = issues.data ?? []
  const closedList = closedIssues.data ?? []
  const prList = prs.data ?? []
  const closedPRList = closedUnmergedPRs.data ?? []
  const runList = runs.data ?? []
  const commitList = commits.data ?? []
  const mergedList = merged.data ?? []

  // Auto-aplica label de tipo (bug/feature/idea/chore/refactor/docs) a issues
  // sin label semántico. Heurística en src/issueType.ts.
  useAutoLabel(repo.slug, issues.data)

  const lastRun = runList[0]
  const ciStatus: CiStatus =
    !lastRun ? 'no-ci'
    : lastRun.status !== 'completed' ? 'pending'
    : lastRun.conclusion === 'success' ? 'success'
    : 'failure'

  const p0Count = issueList.filter((i) => getSeverity(i) === ('p0' satisfies Severity)).length
  // Orphans (PR/merge sin issue resolvable) NO se renderizan — eran ruido en TV.
  // buildThreads ordena por bucket: open arriba, ready-to-report, reported abajo.
  const { threads } = buildThreads(issueList, prList, closedPRList, mergedList, closedList)

  // Issues abiertos (sin merge) siempre visibles; los mergeados se colapsan a
  // los MERGED_VISIBLE más recientes con un botón "mostrar más".
  const openThreads = threads.filter((t) => t.merges.length === 0)
  const mergedThreads = threads.filter((t) => t.merges.length > 0)
  const visibleMerged = expanded ? mergedThreads : mergedThreads.slice(0, MERGED_VISIBLE)
  const hiddenCount = mergedThreads.length - visibleMerged.length

  return (
    <section
      data-testid={`project-${repo.slug}`}
      className={`flex flex-col ${isFirst ? '' : 'border-t-2 border-zinc-900'}`}
    >
      <ProjectHeader
        repo={repo}
        mode={mode}
        slotIndex={slotIndex}
        ciStatus={ciStatus}
        p0Count={p0Count}
        commitCount={commitList.filter((c) => Date.now() - new Date(c.commit.author.date).getTime() < DAY_MS).length}
        carouselTotal={carouselTotal}
      />

      <div data-testid="project-body" className="flex flex-col">
        {threads.length === 0 && (
          <p className="text-xs 4k:text-sm text-zinc-600 py-2 text-center">
            Sin actividad reciente
          </p>
        )}

        {openThreads.map((t) => (
          <ProjectRow key={t.key} thread={t} repo={repo} runs={runList} />
        ))}
        {visibleMerged.map((t) => (
          <ProjectRow key={t.key} thread={t} repo={repo} runs={runList} />
        ))}
        {mergedThreads.length > MERGED_VISIBLE && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`show-more-merges-${repo.slug}`}
            data-state={expanded ? 'expanded' : 'collapsed'}
            className="flex items-center justify-center gap-1.5 py-1 4k:py-1.5 text-[10px] 4k:text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-300 border-b border-zinc-900/60 transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3 4k:h-4 4k:w-4 shrink-0" />
                mostrar menos
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 4k:h-4 4k:w-4 shrink-0" />
                mostrar {hiddenCount} mergeado{hiddenCount === 1 ? '' : 's'} más
              </>
            )}
          </button>
        )}
      </div>
    </section>
  )
}

// Header del proyecto: ci-dot + nombre (botón = toggle pin) y, en modo carrusel,
// controles ◂ rota·N ▸ para rotar manual. Extraído para mantener ProjectSection
// bajo el límite de 150 líneas (regla compactness).
function ProjectHeader({
  repo,
  mode,
  slotIndex,
  ciStatus,
  p0Count,
  commitCount,
  carouselTotal,
}: {
  repo: Repo
  mode: 'pinned' | 'carousel'
  slotIndex: number
  ciStatus: CiStatus
  p0Count: number
  commitCount: number
  carouselTotal?: number
}) {
  return (
    <header
      data-testid="project-header"
      className="px-3 4k:px-6 py-0.5 4k:py-1 flex items-center justify-between gap-3 bg-zinc-950/60"
    >
      <button
        type="button"
        onClick={() => togglePin(repo.slug)}
        aria-label={mode === 'pinned' ? `Despinar ${repo.name}` : `Pinear ${repo.name}`}
        data-testid={`project-header-${repo.slug}`}
        className="flex items-center gap-2 4k:gap-3 group hover:opacity-80 active:opacity-60 transition-opacity min-w-0"
      >
        <span
          className={`h-2.5 w-2.5 4k:h-3.5 4k:w-3.5 rounded-full shadow-md shrink-0 ${
            ciStatus === 'success' ? 'bg-green-500'
            : ciStatus === 'failure' ? 'bg-red-500'
            : ciStatus === 'pending' ? 'bg-amber-500'
            : 'bg-zinc-700'
          }`}
          data-testid="ci-dot"
          data-status={ciStatus}
          title={
            ciStatus === 'no-ci' ? 'Sin workflows configurados'
            : ciStatus === 'pending' ? 'CI en curso'
            : ciStatus === 'failure' ? 'CI falló'
            : 'CI verde'
          }
        />
        <h2
          className={`text-lg 4k:text-2xl font-bold tracking-tight ${repo.color}`}
          data-testid={`project-header-name-${repo.slug}`}
        >
          {repo.name}
        </h2>
        {mode === 'pinned' ? (
          <Pin className="h-3.5 w-3.5 4k:h-5 4k:w-5 text-zinc-300 fill-zinc-300 shrink-0" data-testid={`pin-icon-${repo.slug}`} />
        ) : (
          <RotateCw className="h-3.5 w-3.5 4k:h-5 4k:w-5 text-zinc-500 group-hover:text-zinc-300 shrink-0" data-testid={`carousel-icon-${repo.slug}`} />
        )}
      </button>
      <div className="flex items-center gap-2 4k:gap-3 shrink-0">
        {mode === 'carousel' && carouselTotal && carouselTotal > 1 && (
          <div className="flex items-center gap-1 4k:gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => rotateSlot(slotIndex, -1)}
              aria-label="Proyecto anterior"
              data-testid={`carousel-prev-${repo.slug}`}
              className="text-zinc-500 hover:text-zinc-200 active:text-zinc-400 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 4k:h-5 4k:w-5" />
            </button>
            <span
              className="text-[10px] 4k:text-xs uppercase tracking-widest text-zinc-500 tabular-nums"
              data-testid="carousel-indicator"
            >
              rota · {carouselTotal}
            </span>
            <button
              type="button"
              onClick={() => rotateSlot(slotIndex, 1)}
              aria-label="Proyecto siguiente"
              data-testid={`carousel-next-${repo.slug}`}
              className="text-zinc-500 hover:text-zinc-200 active:text-zinc-400 transition-colors"
            >
              <ChevronRight className="h-4 w-4 4k:h-5 4k:w-5" />
            </button>
          </div>
        )}
        {p0Count > 0 && (
          <span className="px-2 py-0.5 4k:px-2.5 4k:py-1 rounded bg-red-500 text-white text-xs 4k:text-sm font-bold">
            {p0Count} P0
          </span>
        )}
        <span className="text-[10px] 4k:text-xs text-zinc-500 font-mono" data-testid="commits-24h-count">
          {commitCount}·24h
        </span>
      </div>
    </header>
  )
}
